import { useContext, useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { Box, Button, Typography } from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";

import NonLoggedInLayout from "../../layout/NonLoggedInLayout";
import ChannelPartnerAuthContext from "../../context/ChannelPartnerAuthContext";
import { channelPartnerGoogleLogin } from "../../services/channelPartnerPortalApi";
import { useToast } from "../../hook/useToast";

const getLoginErrorMessage = (error) => {
  const data = error?.response?.data;
  const errors = data?.errors || data;
  if (errors && typeof errors === "object") {
    const firstError = Object.values(errors).find(Array.isArray)?.[0];
    if (firstError) return firstError;
  }
  return errors?.detail || data?.message || "Google login failed";
};

const ChannelPartnerLoginPage = () => {
  const { showToast } = useToast();
  const { channelPartnerUser, loginChannelPartner } = useContext(
    ChannelPartnerAuthContext,
  );
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const completeGoogleLogin = async (code) => {
    if (!code) {
      showToast("Google sign-in was cancelled", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await channelPartnerGoogleLogin({ code });
      const accessToken = response?.data?.token?.access;
      if (!accessToken) {
        showToast("Login failed", "error");
        return;
      }
      loginChannelPartner(
        accessToken,
        response.data.user,
        response.data.channel_partner,
      );
      showToast("Channel partner Google login successful", "success");
      navigate("/channel-partner-portal");
    } catch (error) {
      showToast(getLoginErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    scope: "openid email profile",
    ux_mode: "popup",
    onSuccess: ({ code }) => completeGoogleLogin(code),
    onError: () => completeGoogleLogin(null),
  });

  if (channelPartnerUser?.role === "ChannelPartner") {
    return <Navigate to="/channel-partner-portal" replace />;
  }

  return (
    <NonLoggedInLayout
      leftImage="/assets/images/admin-dashboard-login.png"
      leftImageAlt="Channel partner portal login"
    >
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ width: "100%", maxWidth: 450, p: 4, borderRadius: "16px", backgroundColor: "#fff" }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: "36px", fontWeight: 700, color: "#2D315F", lineHeight: 1.1 }}>
              Channel Partner
            </Typography>
            <Typography sx={{ fontSize: "36px", fontWeight: 700, color: "#FFB04A", lineHeight: 1.1 }}>
              Portal
            </Typography>
            <Box sx={{ width: 60, height: 3, bgcolor: "#2D315F", mt: 1, borderRadius: 2 }} />
          </Box>

          <Typography sx={{ fontSize: "14px", color: "rgba(129, 143, 171, 1)", mb: 3, fontWeight: 500 }}>
            Continue with the Google account registered by the administrator.
          </Typography>

          <Button
            type="button"
            disabled={loading}
            onClick={() => googleLogin()}
            fullWidth
            variant="outlined"
            sx={{
              py: 1.2,
              borderRadius: "12px",
              borderColor: "#ECEFF4",
              color: "#5D667A",
              backgroundColor: "#fff",
              fontSize: "15px",
              fontWeight: 600,
              textTransform: "none",
              gap: 1,
              "&:hover": { borderColor: "#D9DEE8", backgroundColor: "#F8FAFC" },
            }}
          >
            <Box component="img" src="/assets/images/google.svg" alt="" sx={{ width: 20, height: 20 }} />
            {loading ? "Signing in..." : "Continue with Google"}
          </Button>
        </Box>
      </Box>
    </NonLoggedInLayout>
  );
};

export default ChannelPartnerLoginPage;
